/**
 * @typedef {import('@/types').FilterOptions} FilterOptions
 * @typedef {import('@/types').FilterSelected} FilterSelected
 * @typedef {import('@/types').FilterOption} FilterOption
 * @typedef {import('@/types').GroupedSelected} GroupedSelected
 */

/** Upstream-first order — used to pick one cascade when multiple filters are cleared. */
const FILTER_SOURCE_ORDER = [
	'college',
	'term',
	'campus',
	'enrollable',
	'format',
	'subject',
	'course',
	'days',
	'startsOn',
];

/** Selection keys that are UI metadata, not searchable filters. */
const NON_FILTER_SELECTION_KEYS = new Set(['term', 'base']);

/**
 * @typedef {{ type: string, index: number, key?: string, selected: boolean }} ActiveFilter
 */

/**
 * Mirrors the filtersSelected computed in App.vue (non-catch-all chips, term excluded).
 *
 * @param {FilterOptions} options
 * @param {FilterSelected} selected
 * @returns {ActiveFilter[]}
 */
function getActiveFilters(options, selected) {
	/** @type {ActiveFilter[]} */
	let filters = Object.entries(selected)
		.filter(([type]) => !NON_FILTER_SELECTION_KEYS.has(type))
		.flatMap(([type, value]) => {
			if (Array.isArray(value)) {
				return value.map((isSelected, index) => ({
					type,
					index,
					selected: /** @type {boolean} */ (isSelected),
				}));
			}

			return Object.entries(/** @type {GroupedSelected} */ (value)).flatMap(([key, group]) => {
				return group.map((isSelected, index) => ({
					type,
					key,
					index,
					selected: isSelected,
				}));
			});
		});

	return filters.filter((filter) => {
		if (!filter.selected) {
			return false;
		}

		if (!Array.isArray(options[filter.type]) && filter.key !== undefined) {
			let optionGroups = /** @type {Record<string, FilterOption[]>} */ (options[filter.type]);
			let option = optionGroups?.[filter.key]?.[filter.index];
			return Boolean(option && option.type !== '*');
		}

		let optionList = /** @type {FilterOption[]} */ (options[filter.type]);
		let option = optionList?.[filter.index];
		return Boolean(option && option.type !== '*');
	});
}

/**
 * @param {Set<string>|string[]} sources
 * @returns {string|null}
 */
function mostUpstreamSource(sources) {
	let sourceList = sources instanceof Set ? [...sources] : sources;

	for (const source of FILTER_SOURCE_ORDER) {
		if (sourceList.includes(source)) {
			return source;
		}
	}

	return null;
}

/**
 * Deselect one chip and re-select catch-all when a group becomes empty.
 *
 * @param {FilterOptions} options
 * @param {FilterSelected} selected
 * @param {{ source: string, index: number, key?: string | null }} selection
 */
export function removeFilterSelection(options, selected, { source, index, key }) {
	if (!Object.hasOwn(selected, source) || selected[source] == null) {
		return;
	}

	if (key !== null && key !== undefined) {
		let grouped = /** @type {GroupedSelected} */ (selected[source]);
		if (!grouped[key]) {
			return;
		}
		grouped[key][index] = false;
	} else if (Array.isArray(selected[source])) {
		let list = /** @type {boolean[]} */ (selected[source]);
		if (index >= list.length) {
			return;
		}
		list[index] = false;
	} else {
		return;
	}

	ensureCatchAllSelected(options, selected, source);
}

/**
 * @param {FilterOptions} options
 * @param {FilterSelected} selected
 * @param {string} source
 */
function ensureCatchAllSelected(options, selected, source) {
	if (Array.isArray(selected[source])) {
		let selectedList = /** @type {boolean[]} */ (selected[source]);
		if (!selectedList.includes(true)) {
			let optionList = /** @type {FilterOption[]} */ (options[source]);
			let catchAllIndex = optionList.findIndex((option) => option.hasOwnProperty('type') && option.type === '*');
			if (catchAllIndex !== -1) {
				selectedList[catchAllIndex] = true;
			}
		}
	} else if (Object.hasOwn(selected[source], '0')) {
		let groupedSelected = /** @type {GroupedSelected} */ (selected[source]);
		let hasSelectedValues = false;
		for (const optionKey in groupedSelected) {
			hasSelectedValues = groupedSelected[optionKey].reduce((carry, current) => carry || current, hasSelectedValues);
		}
		if (hasSelectedValues === false && groupedSelected['0']) {
			groupedSelected['0'][0] = true;
		}
	}
}

/**
 * @param {FilterOptions} options
 * @param {FilterSelected} selected
 * @param {string} source
 */
function resetSourceToCatchAll(options, selected, source) {
	if (!Object.hasOwn(selected, source) || selected[source] == null) {
		return;
	}

	if (Array.isArray(selected[source]) && Array.isArray(options[source])) {
		let optionList = /** @type {FilterOption[]} */ (options[source]);
		selected[source] = optionList.map((option) => {
			return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
		});
		return;
	}

	let groupedSelected = /** @type {GroupedSelected} */ (selected[source]);
	let groupedOptions = /** @type {Record<string, FilterOption[]>} */ (options[source]);
	if (!Object.hasOwn(groupedSelected, '0') || !groupedOptions?.['0']) {
		return;
	}

	for (const key of Object.keys(groupedSelected)) {
		let optionGroup = groupedOptions[key] ?? [];
		groupedSelected[key] = optionGroup.map(() => false);
	}

	groupedSelected['0'][0] = true;
}

/**
 * Clear all active filter chips in one patch, then return which cascade to run.
 *
 * @param {FilterOptions} options
 * @param {FilterSelected} selected
 * @param {{ lockCollege?: boolean }} [config]
 * @returns {{ cascadeSource: string|null, affectedSources: string[] }}
 */
export function removeAllFilters(options, selected, config = {}) {
	let { lockCollege = false } = config;
	let activeFilters = getActiveFilters(options, selected);
	let affectedSources = new Set();

	for (const filter of activeFilters) {
		if (lockCollege && filter.type === 'college') {
			continue;
		}
		affectedSources.add(filter.type);
	}

	for (const source of affectedSources) {
		resetSourceToCatchAll(options, selected, source);
	}

	return {
		cascadeSource: mostUpstreamSource(affectedSources),
		affectedSources: [...affectedSources],
	};
}
